import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { PersonaService, ManagedVendorService } from '@shared/service';

interface TabItem {
  label: string;
  path: string;
  iconKey: string;
}

/**
 * Mobile bottom tab bar (mockup `.tabbar` / `.ctabbar`), shown ≤960px. Vendor and
 * customer personas get distinct tab sets; admin has none (web-first console).
 */
@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  styleUrl: './bottom-nav.component.scss',
  imports: [CommonModule, RouterModule],
  templateUrl: './bottom-nav.component.html',
  
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomNavComponent {
  readonly personaSvc = inject(PersonaService);
  readonly mv = inject(ManagedVendorService);

  /**
   * Optional override supplied by the layout so a guest browsing /customer (null persona)
   * still gets the customer tab set. Falls back to the real persona when unset.
   */
  readonly mode = input<'vendor' | 'admin' | 'customer' | null>(null);

  /** Effective mode: the layout-supplied override, else the logged-in persona. */
  readonly effMode = computed(() => this.mode() ?? this.personaSvc.currentPersona());

  private readonly vendorTabs: TabItem[] = [
    { label: 'Home',     path: '/vendor/dashboard', iconKey: 'home' },
    { label: 'Booking',  path: '/vendor/bookings',  iconKey: 'calendar' },
    { label: 'Wallet',   path: '/vendor/wallet',    iconKey: 'wallet' },
    { label: 'Services', path: '/vendor/services',  iconKey: 'grid' },
    { label: 'Partners', path: '/vendor/partners',  iconKey: 'people' },
  ];

  private readonly customerTabs: TabItem[] = [
    { label: 'Discover',    path: '/customer/discover', iconKey: 'search' },
    { label: 'My bookings', path: '/customer/bookings', iconKey: 'calendar' },
    { label: 'Majlis',      path: '/customer/invite',   iconKey: 'envelope' },
    { label: 'Profile',     path: '/customer/profile',  iconKey: 'user' },
  ];

  readonly items = computed<TabItem[]>(() => {
    switch (this.effMode()) {
      case 'vendor':   return this.vendorTabs;
      case 'customer': return this.customerTabs;
      default:         return [];
    }
  });
}
